import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowUpRight, CalendarRange, KanbanSquare } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import {
  useBoardActivity,
  useBoardFeatures,
  useCreateBoardFeature,
  useMilestones,
  useMvpComponents,
  useUpdateBoardFeature,
  useWorkstreams,
} from "@/application/use-cases/board/use-board.queries";
import type { CreateBoardFeatureInput } from "@/infrastructure/repositories/board.supabase.repository";
import { useStakeholdersList } from "@/application/use-cases/stakeholders/use-stakeholders.queries";
import { useWorkspace } from "@/presentation/providers/workspace.provider";
import { ViewOnlyBadge } from "@/presentation/components/shell/view-only-badge";
import { BoardActivityFeed } from "@/presentation/components/board/board-activity-feed";
import { BoardFeatureDrawerContent } from "@/presentation/components/board/board-feature-drawer-content";
import { BoardFeatureTable } from "@/presentation/components/board/board-feature-table";
import { BoardGroup } from "@/presentation/components/board/board-group";
import { BoardSkeleton } from "@/presentation/components/board/board-skeleton";
import { BoardToolbar, type BoardFilters } from "@/presentation/components/board/board-toolbar";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import {
  COLLAPSED_GROUPS_STORAGE_KEY,
  COLUMN_ORDER_STORAGE_KEY,
  DEFAULT_COLUMN_ORDER,
  DELIVERY_STATUS_LABEL,
  DELIVERY_STATUS_ORDER,
  IMPL_KIND_LABEL,
  STAGE_LABEL,
  STAGE_ORDER,
  buildGridTemplate,
  isDoneStatus,
  nextSort,
  resolveColumns,
  type BoardGroupBy,
  type BoardSort,
} from "@/presentation/data/board";
import { DOMAIN_COLOR } from "@/presentation/data/domains";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@monorepo-apps/ui/src/components/sheet";
import type {
  BoardFeatureRow as BoardFeature,
  DeliveryStatus,
  MilestoneRow,
  MvpComponentRow,
  WorkstreamRow,
} from "@/infrastructure/supabase/database.types";

const boardSearchSchema = z.object({
  item: z.string().optional(),
});

export const Route = createFileRoute("/_app/board")({
  validateSearch: boardSearchSchema,
  component: BoardPage,
});

const STATUS_ACCENT: Record<DeliveryStatus, string> = {
  "not-started": "var(--muted-foreground)",
  "in-progress": "var(--rag-amber)",
  blocked: "var(--rag-red)",
  "in-review": "var(--rag-cyan)",
  done: "var(--rag-green)",
  deferred: "var(--muted-foreground)",
};

interface GroupView {
  id: string;
  eyebrow: string;
  title: string;
  subtitle?: string | null;
  accent: string;
  features: BoardFeature[];
}

interface PostgrestLikeError {
  code?: string;
  message?: string;
}

/**
 * Render a create failure as something a human can act on.
 *
 * supabase-js hands back a PLAIN `{ code, message, details, hint }` object, not
 * an `Error` — it only constructs `PostgrestError` on the `throwOnError` path,
 * which this app never enables. So `instanceof Error` is false here and reading
 * `.message` structurally is the only way to keep the diagnostic; otherwise the
 * toast reads "[object Object]" and tells the user nothing.
 */
function createErrorText(err: unknown): string {
  if (typeof err === "string" && err) return err;
  const e = typeof err === "object" && err !== null ? (err as PostgrestLikeError) : null;
  // Keep this pre-0131 probe in step with the identical one in board.mjs (cmdNew).
  if (e?.code === "23502" && /column "code"/.test(e.message ?? "")) {
    return "the board database is missing migration 0131 (server-side BF-### minting) — ask the team to apply it";
  }
  if (e?.code === "42501" || /row-level security/i.test(e?.message ?? "")) {
    return "you don't have write access to the board";
  }
  if (typeof e?.message === "string" && e.message) return e.message;
  return "unknown error";
}

function BoardPage() {
  const workstreamsQ = useWorkstreams();
  const milestonesQ = useMilestones();
  const componentsQ = useMvpComponents();
  const featuresQ = useBoardFeatures();
  const stakeholdersQ = useStakeholdersList();
  const update = useUpdateBoardFeature();
  const createFeature = useCreateBoardFeature();
  const { canWrite } = useWorkspace();
  const readOnly = !canWrite;

  const navigate = useNavigate({ from: Route.fullPath });
  const { item: openId } = Route.useSearch();

  const workstreams = workstreamsQ.data ?? [];
  const milestones = milestonesQ.data ?? [];
  const components = componentsQ.data ?? [];
  const features = featuresQ.data ?? [];
  const stakeholders = stakeholdersQ.data ?? [];

  const [groupBy, setGroupBy] = useState<BoardGroupBy>("workstream");
  const [sort, setSort] = useState<BoardSort | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const activityQ = useBoardActivity({ enabled: activityOpen });
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(COLUMN_ORDER_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.every((k) => typeof k === "string")) return parsed;
    } catch {
      // ignore malformed storage
    }
    return DEFAULT_COLUMN_ORDER;
  });
  const orderedColumns = useMemo(() => resolveColumns(columnOrder), [columnOrder]);
  const gridTemplate = useMemo(() => buildGridTemplate(orderedColumns), [orderedColumns]);
  const handleReorderColumns = (keys: string[]) => {
    setColumnOrder(keys);
    try {
      localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(keys));
    } catch {
      // ignore storage write failures
    }
  };
  // Collapsed groups, keyed "<groupBy>:<groupId>" so the same id on two axes (a
  // status value vs a workstream key) can't collide. Persisted across visits.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(COLLAPSED_GROUPS_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed)) return new Set(parsed.filter((k) => typeof k === "string"));
    } catch {
      // ignore malformed storage
    }
    return new Set<string>();
  });
  const collapseKey = (groupId: string) => `${groupBy}:${groupId}`;
  const persistCollapsed = (next: Set<string>) => {
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSED_GROUPS_STORAGE_KEY, JSON.stringify([...next]));
    } catch {
      // ignore storage write failures
    }
  };
  const handleGroupOpenChange = (groupId: string, open: boolean) => {
    const next = new Set(collapsed);
    if (open) next.delete(collapseKey(groupId));
    else next.add(collapseKey(groupId));
    persistCollapsed(next);
  };

  const [filters, setFilters] = useState<BoardFilters>({
    search: "",
    status: "all",
    domain: "all",
    criticalOnly: false,
  });

  const componentsById = useMemo(() => new Map(components.map((c) => [c.id, c])), [components]);
  const workstreamCodeByKey = useMemo(
    () => new Map(workstreams.map((w) => [w.key, w.code])),
    [workstreams]
  );
  const activityLookups = useMemo(
    () => ({
      stakeholdersById: new Map(stakeholders.map((s) => [s.id, s.name])),
      componentsById,
      milestonesByKey: new Map(milestones.map((m) => [m.key, m])),
      workstreamsByKey: new Map(workstreams.map((w) => [w.key, w])),
    }),
    [stakeholders, componentsById, milestones, workstreams]
  );

  // Stable mutate fns (the mutation object is recreated each render; .mutate is not).
  const updateMutate = update.mutate;

  // handlePatch is a memoized row prop, so it must stay referentially stable —
  // it reads the current features from a ref rather than closing over them.
  const featuresRef = useRef(features);
  useEffect(() => {
    featuresRef.current = features;
  });

  const handlePatch = useCallback(
    (id: string, patch: Partial<BoardFeature>) => {
      if (readOnly) return;
      // Moving an item to another block via the Block field: land it at the end
      // of the destination. Without this it keeps the sort_order it held in its
      // old block and wedges itself into the middle of the new one, which reads
      // as the move having gone somewhere random.
      if (patch.workstream_key !== undefined && patch.sort_order === undefined) {
        const target = patch.workstream_key;
        const last = featuresRef.current.reduce(
          (max, f) =>
            f.id !== id && f.workstream_key === target ? Math.max(max, f.sort_order) : max,
          0
        );
        patch = { ...patch, sort_order: last + 1 };
      }
      updateMutate({ id, patch });
    },
    [updateMutate, readOnly]
  );
  const handleOpen = useCallback((id: string) => navigate({ search: { item: id } }), [navigate]);
  const handleDragEnd = useCallback(() => setDraggedId(null), []);
  const handleToggleSort = useCallback((key: string) => setSort((s) => nextSort(s, key)), []);

  // handleDrop must stay referentially stable (it's a memoized row prop), but it
  // reads values that change every edit — so it reads them from a ref, kept fresh
  // in an effect (writing a ref during render is disallowed).
  const dragCtx = useRef({ features, groupBy, componentsById, draggedId });
  useEffect(() => {
    dragCtx.current = { features, groupBy, componentsById, draggedId };
  });

  // Drag a feature onto another to reorder within its group (fractional
  // sort_order) and, when grouped by workstream/component/milestone/status,
  // adopt the drop target's group field — i.e. move it to that block/etc.
  const handleDrop = useCallback(
    (targetId: string, position: "before" | "after") => {
      if (readOnly) return;
      const { features, groupBy, componentsById, draggedId } = dragCtx.current;
      if (!draggedId || draggedId === targetId) return;
      const dragged = features.find((f) => f.id === draggedId);
      const target = features.find((f) => f.id === targetId);
      if (!dragged || !target) return;

      const patch: Partial<BoardFeature> = {};
      // Cross-group move: adopt the target's group field (stage is derived → no move).
      if (groupBy === "workstream" && dragged.workstream_key !== target.workstream_key) {
        patch.workstream_key = target.workstream_key;
      } else if (groupBy === "component" && dragged.component_id !== target.component_id) {
        patch.component_id = target.component_id;
      } else if (groupBy === "milestone" && dragged.milestone_key !== target.milestone_key) {
        patch.milestone_key = target.milestone_key;
      } else if (groupBy === "status" && dragged.delivery_status !== target.delivery_status) {
        patch.delivery_status = target.delivery_status;
      }

      // Order: midpoint between the target's neighbours in its group.
      const key = featureGroupKey(groupBy, target, componentsById);
      const members = features
        .filter((f) => f.id !== draggedId && featureGroupKey(groupBy, f, componentsById) === key)
        .sort((a, b) => a.sort_order - b.sort_order);
      const tIdx = members.findIndex((f) => f.id === targetId);
      const insertAt = position === "before" ? tIdx : tIdx + 1;
      const prev = members[insertAt - 1];
      const next = members[insertAt];
      let sort_order: number;
      if (prev && next) sort_order = (prev.sort_order + next.sort_order) / 2;
      else if (prev) sort_order = prev.sort_order + 1;
      else if (next) sort_order = next.sort_order - 1;
      else sort_order = target.sort_order;
      patch.sort_order = sort_order;

      updateMutate({ id: draggedId, patch });
      setDraggedId(null);
    },
    [updateMutate, readOnly]
  );

  // The insert leaves `code` unset — the DB mints the next BF-### (migration
  // 0131), which is the only writer that can guarantee no repeats. A failed
  // insert must be visible: an item that silently never appears reads as the
  // board being broken.
  const submitCreate = (input: CreateBoardFeatureInput) => {
    createFeature.mutate(input, {
      onSuccess: (row) => toast.success(`Added ${row.code} — ${row.title}`),
      onError: (err) =>
        toast.error("Could not add the item", {
          description: `“${input.title}” — ${createErrorText(err)}`,
        }),
    });
  };

  // "+ Add item": create a feature that lands in the group it was added under,
  // for whatever the current grouping is. workstream_key is required, so it's
  // derived from the group (or a sibling) in every case.
  const handleAddFeature = (group: GroupView, title: string) => {
    if (readOnly) return;
    const sib = group.features[group.features.length - 1];
    const groupId = group.id === "__none__" ? null : group.id;
    // Empty group: only the workstream axis renders feature-less groups, and there
    // the group id IS the workstream_key — so the first item can be seeded without
    // a sibling to inherit from.
    if (!sib) {
      // Defence-in-depth: buildGroups only renders a feature-less group on the
      // workstream axis, so this is unreachable today. Report rather than return
      // silently — but don't tell the user to do something, since if the
      // invariant ever breaks the cause won't be anything they did.
      if (groupBy !== "workstream" || !groupId) {
        toast.error("Could not work out which workstream this item belongs to.", {
          description: `“${title}” was not added.`,
        });
        return;
      }
      submitCreate({ title, workstream_key: groupId, sort_order: 0 });
      return;
    }
    const input: CreateBoardFeatureInput = {
      title,
      workstream_key: sib.workstream_key,
      sort_order: sib.sort_order + 1,
    };
    switch (groupBy) {
      case "workstream":
        if (groupId) input.workstream_key = groupId;
        break;
      case "component":
        input.component_id = groupId;
        input.workstream_key =
          (groupId ? componentsById.get(groupId)?.primary_workstream_key : null) ??
          sib.workstream_key;
        break;
      case "milestone":
        input.milestone_key = groupId;
        break;
      case "status":
        if (groupId) input.delivery_status = groupId as DeliveryStatus;
        break;
      case "stage":
        input.component_id = sib.component_id;
        break;
    }
    submitCreate(input);
  };

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return features.filter((f) => {
      if (filters.status !== "all" && f.delivery_status !== filters.status) return false;
      if (filters.criticalOnly && !f.mvp_blocking) return false;
      if (filters.domain !== "all") {
        const dom = f.component_id ? componentsById.get(f.component_id)?.domain : null;
        if (dom !== filters.domain) return false;
      }
      if (q) {
        const hay = `${f.title} ${f.description ?? ""} ${
          f.component_id ? (componentsById.get(f.component_id)?.name_en ?? "") : ""
        }`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [features, filters, componentsById]);

  const groups = useMemo(
    () =>
      buildGroups(
        groupBy,
        filtered,
        {
          workstreams,
          milestones,
          components,
          componentsById,
          stakeholdersById: activityLookups.stakeholdersById,
          milestonesByKey: activityLookups.milestonesByKey,
        },
        sort
      ),
    [groupBy, filtered, workstreams, milestones, components, componentsById, activityLookups, sort]
  );

  const allCollapsed = groups.length > 0 && groups.every((g) => collapsed.has(collapseKey(g.id)));
  const handleToggleAllGroups = () => {
    const next = new Set(collapsed);
    for (const g of groups) {
      if (allCollapsed) next.delete(collapseKey(g.id));
      else next.add(collapseKey(g.id));
    }
    persistCollapsed(next);
  };

  const stats = useMemo(() => {
    const done = features.filter((f) => isDoneStatus(f.delivery_status)).length;
    const inProgress = features.filter((f) => f.delivery_status === "in-progress").length;
    const blocked = features.filter((f) => f.delivery_status === "blocked").length;
    const critical = features.filter((f) => f.mvp_blocking).length;
    return { total: features.length, done, inProgress, blocked, critical };
  }, [features]);

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-[100rem] min-w-0 flex-col gap-8 p-6 lg:gap-10 lg:p-10">
      <RouteHero
        eyebrow="MVP Delivery Board"
        icon={KanbanSquare}
        numeral={String(stats.total).padStart(3, "0")}
        numeralTone="cyan"
        numeralSuffix="features"
        title="Every MVP feature, sequenced by the build workplan"
        summary="Buildable units derived from the Phase-1 work assumptions, grouped by workstream (Blocks 0 → 3) and aligned to milestones M0–M8. Track each unit from not-started to done across the ~16-week build."
        collapsibleSummary
        meta={[
          { label: "Done", value: stats.done, tone: "green" },
          { label: "In progress", value: stats.inProgress, tone: "amber" },
          { label: "Blocked", value: stats.blocked, tone: "red" },
          { label: "Critical path", value: stats.critical, tone: "cyan" },
        ]}
        actions={
          <>
            {readOnly ? <ViewOnlyBadge /> : null}
            <Link
              to="/planner"
              className="group inline-flex items-center justify-between gap-2 rounded-lg border border-[oklch(1_0_0_/_0.12)] bg-[oklch(1_0_0_/_0.04)] px-4 py-2.5 text-[13px] font-medium text-[oklch(0.93_0.008_240)] transition-all hover:border-[oklch(0.72_0.17_190_/_0.5)] hover:bg-[oklch(0.72_0.17_190_/_0.08)] print:hidden"
            >
              <span className="inline-flex items-center gap-2">
                <CalendarRange className="size-3.5" />
                <span className="font-data text-[11px] tracking-[0.12em] uppercase">
                  Open Build Planner
                </span>
              </span>
              <ArrowUpRight className="size-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </>
        }
      />

      <BoardToolbar
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
        filters={filters}
        onFiltersChange={setFilters}
        onOpenActivity={() => setActivityOpen(true)}
        allCollapsed={allCollapsed}
        onToggleAllGroups={handleToggleAllGroups}
      />

      {featuresQ.isLoading ? (
        <BoardSkeleton />
      ) : groups.length === 0 ? (
        <p className="text-muted-foreground text-[13px]">No features match the current filters.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map((g) => {
            const done = g.features.filter((f) => isDoneStatus(f.delivery_status)).length;
            const blocking = g.features.filter(
              (f) => f.mvp_blocking && !isDoneStatus(f.delivery_status)
            ).length;
            return (
              <BoardGroup
                key={g.id}
                eyebrow={g.eyebrow}
                title={g.title}
                subtitle={g.subtitle}
                total={g.features.length}
                done={done}
                blocking={blocking}
                accent={g.accent}
                open={!collapsed.has(collapseKey(g.id))}
                onOpenChange={(open) => handleGroupOpenChange(g.id, open)}
              >
                <BoardFeatureTable
                  features={g.features}
                  orderedColumns={orderedColumns}
                  gridTemplate={gridTemplate}
                  onReorderColumns={handleReorderColumns}
                  sort={sort}
                  onToggleSort={handleToggleSort}
                  components={components}
                  componentsById={componentsById}
                  milestones={milestones}
                  stakeholders={stakeholders}
                  workstreams={workstreams}
                  onPatch={handlePatch}
                  onOpen={handleOpen}
                  draggedId={draggedId}
                  onDragStart={setDraggedId}
                  onDragEnd={handleDragEnd}
                  onDropRelative={handleDrop}
                  workstreamCodeByKey={workstreamCodeByKey}
                  onAddFeature={(title) => handleAddFeature(g, title)}
                  readOnly={readOnly}
                />
              </BoardGroup>
            );
          })}
        </div>
      )}

      <Sheet
        open={!!openId}
        onOpenChange={(open) => {
          if (!open) navigate({ search: {} });
        }}
      >
        <SheetContent side="right" className="w-full p-0 sm:max-w-2xl">
          <SheetTitle className="sr-only">Feature detail</SheetTitle>
          <SheetDescription className="sr-only">
            {readOnly
              ? "View the delivery status, milestone, owner and notes for this feature."
              : "View and edit the delivery status, milestone, owner and notes for this feature."}
          </SheetDescription>
          {openId ? (
            <BoardFeatureDrawerContent
              id={openId}
              onClose={() => navigate({ search: {} })}
              readOnly={readOnly}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      <Sheet open={activityOpen} onOpenChange={setActivityOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-lg">
          <div className="border-border/60 border-b px-6 py-5">
            <SheetTitle className="text-[15px] font-semibold tracking-tight">
              Board activity
            </SheetTitle>
            <SheetDescription className="text-muted-foreground mt-1 text-[12px]">
              Every change across the board, newest first — who did what, and when.
            </SheetDescription>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-3">
            <BoardActivityFeed
              entries={(activityQ.data?.pages ?? []).flatMap((p) => p.rows)}
              isLoading={activityQ.isLoading}
              lookups={activityLookups}
              showItem
              onOpenItem={(featureId) => {
                setActivityOpen(false);
                navigate({ search: { item: featureId } });
              }}
              hasMore={activityQ.hasNextPage}
              isLoadingMore={activityQ.isFetchingNextPage}
              onLoadMore={() => activityQ.fetchNextPage()}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

interface Lookups {
  workstreams: WorkstreamRow[];
  milestones: MilestoneRow[];
  components: MvpComponentRow[];
  componentsById: Map<string, MvpComponentRow>;
  stakeholdersById: Map<string, string>;
  milestonesByKey: Map<string, MilestoneRow>;
}

// Sorts strings/numbers naturally; null-ish values always sort last (asc).
const LAST = "￿";

// Numeric collation, so BF-999 sorts before BF-1000 once codes go 4 digits wide
// (migration 0131 stops padding past 999). Hoisted: this runs inside a sort
// comparator, and localeCompare with an options object can build a fresh
// collator per call.
const COLLATOR = new Intl.Collator(undefined, { numeric: true });

function sortValue(key: string, f: BoardFeature, lk: Lookups): number | string {
  switch (key) {
    case "item":
      return f.code ?? LAST;
    // Board order, not alphabetical — Block 0 before Block 1, etc.
    case "block":
      return (
        lk.workstreams.find((w) => w.key === f.workstream_key)?.sort_order ??
        Number.MAX_SAFE_INTEGER
      );
    case "status":
      return DELIVERY_STATUS_ORDER.indexOf(f.delivery_status);
    case "pr":
      return f.pr_number ?? Number.POSITIVE_INFINITY;
    case "owner":
      return f.owner_id ? (lk.stakeholdersById.get(f.owner_id) ?? LAST).toLowerCase() : LAST;
    case "milestone":
      return f.milestone_key
        ? (lk.milestonesByKey.get(f.milestone_key)?.sort_order ?? Number.POSITIVE_INFINITY)
        : Number.POSITIVE_INFINITY;
    case "due":
      return f.due_date ? new Date(f.due_date + "T00:00:00").getTime() : Number.POSITIVE_INFINITY;
    case "component":
      return f.component_id
        ? (lk.componentsById.get(f.component_id)?.name_en ?? LAST).toLowerCase()
        : LAST;
    case "impl":
      return f.impl_kind ? IMPL_KIND_LABEL[f.impl_kind] : LAST;
    case "domain":
      return f.component_id ? (lk.componentsById.get(f.component_id)?.domain ?? LAST) : LAST;
    case "critical":
      return f.mvp_blocking ? 0 : 1;
    default:
      return f.sort_order;
  }
}

function compareBySort(a: BoardFeature, b: BoardFeature, sort: BoardSort, lk: Lookups): number {
  const av = sortValue(sort.key, a, lk);
  const bv = sortValue(sort.key, b, lk);
  let cmp =
    typeof av === "number" && typeof bv === "number"
      ? av - bv
      : COLLATOR.compare(String(av), String(bv));
  if (cmp === 0) cmp = a.sort_order - b.sort_order; // stable tiebreak on manual order
  return sort.dir === "asc" ? cmp : -cmp;
}

function featureGroupKey(
  groupBy: BoardGroupBy,
  f: BoardFeature,
  componentsById: Map<string, MvpComponentRow>
): string {
  switch (groupBy) {
    case "workstream":
      return f.workstream_key;
    case "component":
      return f.component_id ?? "__none__";
    case "milestone":
      return f.milestone_key ?? "__none__";
    case "stage":
      return (f.component_id ? componentsById.get(f.component_id)?.stage : null) ?? "__none__";
    case "status":
      return f.delivery_status;
  }
}

function buildGroups(
  groupBy: BoardGroupBy,
  features: BoardFeature[],
  lk: Lookups,
  sort: BoardSort | null
): GroupView[] {
  const byKey = new Map<string, BoardFeature[]>();
  for (const f of features) {
    const key = featureGroupKey(groupBy, f, lk.componentsById);
    const list = byKey.get(key);
    if (list) list.push(f);
    else byKey.set(key, [f]);
  }
  // Within each group: a column sort (if active) overrides the manual order;
  // otherwise order by sort_order so drag-reorder reflects optimistically.
  for (const list of byKey.values()) {
    if (sort) list.sort((a, b) => compareBySort(a, b, sort, lk));
    else list.sort((a, b) => a.sort_order - b.sort_order);
  }

  const groups: GroupView[] = [];
  const pushNone = () => {
    const none = byKey.get("__none__");
    if (none && none.length > 0) {
      groups.push({
        id: "__none__",
        eyebrow: "Unassigned",
        title: "Unassigned",
        accent: "var(--muted-foreground)",
        features: none,
      });
    }
  };

  if (groupBy === "workstream") {
    // Every workstream renders as a group — including feature-less ones just added
    // from the timeline — so a new workstream is immediately visible on the board
    // as an empty column ready to receive items.
    for (const w of lk.workstreams) {
      groups.push({
        id: w.key,
        eyebrow: `Block ${w.code}`,
        title: w.title,
        subtitle: w.summary,
        accent: "var(--primary)",
        features: byKey.get(w.key) ?? [],
      });
    }
    pushNone();
  } else if (groupBy === "component") {
    for (const c of lk.components) {
      const f = byKey.get(c.id);
      if (!f || f.length === 0) continue;
      groups.push({
        id: c.id,
        eyebrow: STAGE_LABEL[c.stage],
        title: c.name_en,
        subtitle: c.role,
        accent: c.domain ? DOMAIN_COLOR[c.domain] : "var(--primary)",
        features: f,
      });
    }
    pushNone();
  } else if (groupBy === "milestone") {
    for (const m of lk.milestones) {
      const f = byKey.get(m.key);
      if (!f || f.length === 0) continue;
      groups.push({
        id: m.key,
        eyebrow: m.code,
        title: m.title,
        subtitle: m.target_date ? `Target ${m.target_date}` : null,
        accent: m.is_gate ? "var(--rag-red)" : "var(--primary)",
        features: f,
      });
    }
    pushNone();
  } else if (groupBy === "stage") {
    for (const s of STAGE_ORDER) {
      const f = byKey.get(s);
      if (!f || f.length === 0) continue;
      groups.push({
        id: s,
        eyebrow: "Lifecycle stage",
        title: STAGE_LABEL[s],
        accent: "var(--primary)",
        features: f,
      });
    }
    pushNone();
  } else {
    for (const s of DELIVERY_STATUS_ORDER) {
      const f = byKey.get(s);
      if (!f || f.length === 0) continue;
      groups.push({
        id: s,
        eyebrow: "Status",
        title: DELIVERY_STATUS_LABEL[s],
        accent: STATUS_ACCENT[s],
        features: f,
      });
    }
  }

  return groups;
}
